import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QuizIcon from "@mui/icons-material/QuizOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryIcon from "@mui/icons-material/History";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBookOutlined";
import useDragReorder from "./useDragReorder";
import { formatBytes, formatDate, plural } from "./format";

const iconSx = { color: "#475569" };

/** One chapter: drag handle, title and details, and every action on it. */
const ChapterRow = ({ chapter, index, total, handleProps, rowProps, reorderable, actions }) => {
  const [menu, setMenu] = useState(null);
  const [download, setDownload] = useState(null);
  const run = (fn) => () => { setMenu(null); setDownload(null); fn(); };

  return (
    <Box {...rowProps}
      sx={{
        display: "flex", alignItems: "center", gap: 1, px: 1, py: 1, borderRadius: 1.5,
        "&:hover": { bgcolor: "#F8FAFC" }, "&:hover .chapter-actions": { opacity: 1 },
      }}>
      <Box {...handleProps} sx={{ cursor: reorderable ? "grab" : "default", display: "flex", color: "#CBD5E1",
        visibility: reorderable ? "visible" : "hidden", "&:hover": { color: "#64748B" } }}
        title="Drag to reorder">
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Typography sx={{ width: 22, color: "#94A3B8", fontWeight: 600, fontSize: 13, textAlign: "right" }}>
        {index + 1}
      </Typography>
      <ArticleIcon sx={{ color: chapter.hidden ? "#CBD5E1" : "#0172A6", fontSize: 20 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: chapter.hidden ? "#94A3B8" : "#0F172A" }} noWrap>
            {chapter.title}
          </Typography>
          {chapter.hidden && <Chip size="small" label="Hidden from students" sx={{ height: 20, fontSize: 11 }} />}
          {!chapter.has_file && <Chip size="small" color="warning" label="No file yet" sx={{ height: 20, fontSize: 11 }} />}
        </Box>
        <Typography sx={{ fontSize: 12, color: "#64748B" }} noWrap>
          {[
            chapter.html_filename,
            chapter.total_size_bytes ? formatBytes(chapter.total_size_bytes) : null,
            chapter.assets_count ? plural(chapter.assets_count, "asset") : null,
            `v${chapter.version}`,
            chapter.updated_at ? `updated ${formatDate(chapter.updated_at)}${chapter.updated_by ? ` by ${chapter.updated_by}` : ""}` : null,
          ].filter(Boolean).join(" · ")}
        </Typography>
      </Box>

      <Box className="chapter-actions" sx={{ display: "flex", alignItems: "center", opacity: { xs: 1, md: 0.55 }, transition: "opacity .15s" }}>
        <Tooltip title="Preview">
          <span><IconButton size="small" disabled={!chapter.has_file} onClick={() => actions.preview(chapter)} aria-label="Preview">
            <VisibilityIcon fontSize="small" sx={iconSx} />
          </IconButton></span>
        </Tooltip>
        <Tooltip title="Upload a new version">
          <IconButton size="small" onClick={() => actions.replace(chapter)} aria-label="Upload a new version">
            <UploadIcon fontSize="small" sx={iconSx} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <span><IconButton size="small" disabled={!chapter.has_file} onClick={(e) => setDownload(e.currentTarget)} aria-label="Download">
            <DownloadIcon fontSize="small" sx={iconSx} />
          </IconButton></span>
        </Tooltip>
        <Tooltip title="More">
          <IconButton size="small" onClick={(e) => setMenu(e.currentTarget)} aria-label="More chapter actions">
            <MoreVertIcon fontSize="small" sx={iconSx} />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu anchorEl={download} open={!!download} onClose={() => setDownload(null)}>
        <MenuItem onClick={run(() => actions.download(chapter, "html"))}>
          <ListItemText primary="HTML package (.zip)" secondary="The page with all its images, styles and fonts" />
        </MenuItem>
        <MenuItem onClick={run(() => actions.download(chapter, "md"))}>
          <ListItemText primary="Clean Markdown (.md)" secondary="Text and maths only, no styling" />
        </MenuItem>
      </Menu>

      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={run(() => actions.rename(chapter))}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Rename
        </MenuItem>
        <MenuItem onClick={run(() => actions.toggleHidden(chapter))}>
          <ListItemIcon>{chapter.hidden ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}</ListItemIcon>
          {chapter.hidden ? "Show to students" : "Hide from students"}
        </MenuItem>
        <MenuItem onClick={run(() => actions.versions(chapter))}>
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>Version history
        </MenuItem>
        <MenuItem onClick={run(() => actions.move(chapter))}>
          <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>Move to another module
        </MenuItem>
        <MenuItem disabled={index === 0} onClick={run(() => actions.shift(chapter, -1))}>
          <ListItemIcon><ArrowUpwardIcon fontSize="small" /></ListItemIcon>Move up
        </MenuItem>
        <MenuItem disabled={index === total - 1} onClick={run(() => actions.shift(chapter, 1))}>
          <ListItemIcon><ArrowDownwardIcon fontSize="small" /></ListItemIcon>Move down
        </MenuItem>
        <Divider />
        <MenuItem onClick={run(() => actions.remove(chapter))} sx={{ color: "#DC2626" }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: "#DC2626" }} /></ListItemIcon>Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

/** One module: header with its actions, and its chapters (drag to reorder). */
const ModuleCard = ({
  module, index, total, expanded, onToggle, handleProps, rowProps, reorderable = true, moduleActions, chapterActions, highlight,
}) => {
  const [menu, setMenu] = useState(null);
  const { order: chapters, handleProps: chapterHandle, rowProps: chapterRow, move } = useDragReorder(
    module.chapters,
    (ids) => chapterActions.reorder(module, ids),
  );
  const run = (fn) => () => { setMenu(null); fn(); };
  const actionsForRow = { ...chapterActions, shift: (chapter, delta) => move(chapter.id, delta) };

  return (
    <Box {...rowProps}
      sx={{
        border: "1px solid", borderColor: highlight ? "#0172A6" : "#E2E8F0", borderRadius: 2, bgcolor: "#fff",
        boxShadow: highlight ? "0 0 0 3px rgba(1,114,166,.15)" : "0 1px 2px rgba(15,23,42,.04)",
      }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25 }}>
        <Box {...handleProps} sx={{ cursor: reorderable ? "grab" : "default", display: "flex", color: "#CBD5E1",
          visibility: reorderable ? "visible" : "hidden", "&:hover": { color: "#64748B" } }}
          title="Drag to reorder modules">
          <DragIndicatorIcon />
        </Box>
        <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: "#E5F1F6", color: "#0172A6", fontWeight: 700,
          fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {index + 1}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onToggle}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0F172A" }} noWrap>{module.name}</Typography>
          <Typography sx={{ fontSize: 12, color: module.chapters.length ? "#64748B" : "#D97706" }}>
            {module.chapters.length ? plural(module.chapters.length, "chapter") : "No chapters yet"}
            {module.chapters.some((c) => c.hidden) ? ` · ${module.chapters.filter((c) => c.hidden).length} hidden` : ""}
          </Typography>
        </Box>
        <Tooltip title="Add a chapter">
          <IconButton size="small" onClick={() => chapterActions.add(module)} aria-label={`Add a chapter to ${module.name}`}>
            <AddIcon fontSize="small" sx={iconSx} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rename module">
          <IconButton size="small" onClick={() => moduleActions.rename(module)} aria-label={`Rename ${module.name}`}>
            <EditIcon fontSize="small" sx={iconSx} />
          </IconButton>
        </Tooltip>
        <Tooltip title="More">
          <IconButton size="small" onClick={(e) => setMenu(e.currentTarget)} aria-label="More module actions">
            <MoreVertIcon fontSize="small" sx={iconSx} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onToggle} aria-label={expanded ? "Collapse" : "Expand"}>
          <ExpandMoreIcon sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
        </IconButton>
      </Box>

      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={run(() => moduleActions.copyFrom(module))}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>Copy chapters from…
        </MenuItem>
        <MenuItem onClick={run(() => moduleActions.practice(module))}>
          <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>Questions & assignments
        </MenuItem>
        <MenuItem disabled={index === 0} onClick={run(() => moduleActions.shift(module, -1))}>
          <ListItemIcon><ArrowUpwardIcon fontSize="small" /></ListItemIcon>Move up
        </MenuItem>
        <MenuItem disabled={index === total - 1} onClick={run(() => moduleActions.shift(module, 1))}>
          <ListItemIcon><ArrowDownwardIcon fontSize="small" /></ListItemIcon>Move down
        </MenuItem>
        <Divider />
        <MenuItem onClick={run(() => moduleActions.remove(module))} sx={{ color: "#DC2626" }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: "#DC2626" }} /></ListItemIcon>Delete module
        </MenuItem>
      </Menu>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ borderTop: "1px solid #F1F5F9", px: 1.5, py: 1 }}>
          {chapters.length === 0 ? (
            <Box sx={{ border: "1px dashed #CBD5E1", borderRadius: 2, py: 3, textAlign: "center", my: 0.5 }}>
              <MenuBookIcon sx={{ color: "#94A3B8" }} />
              <Typography sx={{ fontSize: 14, color: "#64748B", mt: 0.5 }}>This module has no chapters yet.</Typography>
              <Button size="small" variant="contained" startIcon={<UploadIcon />} sx={{ mt: 1.5 }}
                onClick={() => chapterActions.add(module)}>
                Upload first chapter
              </Button>
            </Box>
          ) : (
            <>
              {chapters.map((chapter, i) => (
                <ChapterRow key={chapter.id} chapter={chapter} index={i} total={chapters.length}
                  reorderable={reorderable}
                  handleProps={reorderable ? chapterHandle(chapter.id) : {}}
                  rowProps={reorderable ? chapterRow(chapter.id) : {}}
                  actions={reorderable ? actionsForRow : { ...actionsForRow, shift: () => {} }} />
              ))}
              <Button size="small" startIcon={<AddIcon />} sx={{ mt: 0.5, ml: 4 }} onClick={() => chapterActions.add(module)}>
                Add chapter
              </Button>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ModuleCard;
